import {
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { CommunityPostOptionsModel } from "client/Api/Communities/communityPosts";
import {
	CommunityPostDetailsPageViewModel,
	CommunityPostDetailsViewModel,
	CommunityPostStatisticsModel,
	getCommunityPostDetails,
	getStatistics
} from "client/Api/Communities/communityPostViewer";
import { HttpRequestError } from "client/Api/httpClient";
import {
	AlertManager,
	useAlerts,
	useSynchronizedRef,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";

export type CommunityContentData = {
	postState: PostLoadingState;
	reloadPostHandler: () => void;
	isFollowingChangedHandler: (isFollowing: boolean) => void;
	statisticsRetrievedHandler: (statistics: CommunityPostStatisticsModel) => void;
	retrievePostStatistics: () => Promise<void>;
};

export function useCommunityPostContent(
	id: number,
	onAfterInitialFetch: () => void
): CommunityContentData {
	const [postState, setPostState] = useState<PostLoadingState>({ loadingStatus: "loading" });
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const onAfterInitialFetchRef: ReadonlyRefObject<typeof onAfterInitialFetch> = useSynchronizedRef(onAfterInitialFetch);

	const fetchItem = useCallback(
		async (signal: AbortSignal, isInitialFetch: boolean = false): Promise<void> => {
			setPostState({ loadingStatus: "loading"});

			try {
				const communityPostDetailsViewModel: CommunityPostDetailsPageViewModel = await getCommunityPostDetails(id, signal);

				setPostState({
					loadingStatus: "loaded",
					options: communityPostDetailsViewModel.options,
					post: communityPostDetailsViewModel.post
				});

				if (isInitialFetch) {
					onAfterInitialFetchRef.current();
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				if (e instanceof HttpRequestError && e.statusCode === 404) {
					setPostState({ loadingStatus: "not-found"});
					alertManager.showError(localize("_ItemNotFound"));
				} else {
					setPostState({ loadingStatus: "error"});
					alertManager.showError(localize("_CannotOpenItem"));
				}
			}
		},
		[
			alertManager,
			id,
			onAfterInitialFetchRef
		]
	);

	useEffect(
		(): () => void => {
			const cancellation = new AbortController();
			fetchItem(cancellation.signal, true);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			fetchItem
		]
	);

	const reloadPostHandler = useCallback(
		(): void => {
			fetchItem(globalCancellation);
		},
		[fetchItem, globalCancellation]
	);

	const isFollowingChangedHandler = useCallback(
		(isFollowing: boolean): void => {
			setPostState(
				(prevState: PostLoadingState): PostLoadingState => {
					if (prevState.loadingStatus !== "loaded") {
						return prevState;
					}

					return {
						...prevState,
						post: {
							...prevState.post,
							isFollowing
						}
					};
				}
			);
		},
		[]
	);

	const statisticsRetrievedHandler = useCallback(
		(statistics: CommunityPostStatisticsModel): void => {
			setPostState(
				(prevState: PostLoadingState): PostLoadingState => {
					if (prevState.loadingStatus !== "loaded") {
						return prevState;
					}

					return {
						...prevState,
						post: {
							...prevState.post,
							statistics
						}
					};
				}
			);
		},
		[]
	);

	const retrievePostStatistics = useCallback(
		async (): Promise<void> => {
			try {
				const newStatistics: CommunityPostStatisticsModel = await getStatistics(
					id,
					globalCancellation
				);

				statisticsRetrievedHandler(newStatistics);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

		},
		[
			alertManager,
			statisticsRetrievedHandler,
			globalCancellation,
			id
		]
	);

	return useMemo(
		(): CommunityContentData => ({
			postState,
			reloadPostHandler,
			isFollowingChangedHandler,
			statisticsRetrievedHandler,
			retrievePostStatistics
		}),
		[
			postState,
			reloadPostHandler,
			isFollowingChangedHandler,
			statisticsRetrievedHandler,
			retrievePostStatistics
		]
	);
}

export type PostLoadingState = {
	loadingStatus: "loaded";
	options: CommunityPostOptionsModel;
	post: CommunityPostDetailsViewModel;
} | {
	loadingStatus: "not-found" | "loading" | "error";
};